import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { FlexiComplianceService } from 'src/app/kuba/flexi/compliance/flexi-compliance.service';
import {
  FlexiComplianceActionPointOverview,
  FlexiComplianceActionPointStatus,
  FlexiComplianceRecurrenceType,
  FlexiComplianceResponsibleParty,
  responsiblePartyLabelKey,
  responsiblePartyNameSuffix
} from 'src/app/kuba/flexi/compliance/flexi-compliance.models';

/**
 * Handlingsplan — the business's outstanding BHT-samsvar commitments.
 *
 * Covers every open action point, recurring and one-off, ordered worst-first by
 * the server. Two things are true at once and the card has to show both: what
 * is still owed, and what has already gone wrong.
 *
 * The compliance markers earn their prominence. A skipped cycle is the one
 * failure nothing else in the product reveals — no action point ever existed
 * for the missed cycle, so neither an overdue badge nor the review score can
 * show it. Those rows sort first and are counted separately.
 *
 * Reporting, never blocking: the system records the exception and puts it in
 * front of a human rather than refusing the completion that caused it.
 */
@Component({
  selector: 'app-flexi-compliance-action-points-widget',
  templateUrl: './flexi-compliance-action-points-widget.component.html',
  styleUrls: ['./flexi-compliance-action-points-widget.component.scss'],
  standalone: false
})
export class FlexiComplianceActionPointsWidgetComponent implements OnInit {
  readonly ApStatus = FlexiComplianceActionPointStatus;
  readonly Recurrence = FlexiComplianceRecurrenceType;

  loading = true;
  rows: FlexiComplianceActionPointOverview[] = [];

  readonly Responsible = FlexiComplianceResponsibleParty;

  constructor(
    private service: FlexiComplianceService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.service.actionPointOverview(BaseServices.BusinessId).subscribe({
      next: rows => {
        this.rows = rows;
        this.loading = false;
      },
      // No assessment / no Flexi → empty state, not an error. The dashboard
      // already gates this card on the business having Flexi at all.
      error: (_err: HttpErrorResponse) => {
        this.rows = [];
        this.loading = false;
      }
    });
  }

  get hasData(): boolean {
    return this.rows.length > 0;
  }

  /**
   * Every outstanding commitment, not a truncated head of the list.
   *
   * The card used to render five and hide the rest behind a "+N more" button,
   * which meant the count in the footer was the only evidence the others
   * existed. They are scrollable now: the card keeps its height, and a reader
   * who wants the sixth row can reach it without leaving the dashboard.
   */
  get visibleRows(): FlexiComplianceActionPointOverview[] {
    return this.rows;
  }

  /** i18n key of the role that owes this action point. */
  responsibleLabelKey(row: FlexiComplianceActionPointOverview): string {
    return responsiblePartyLabelKey(row.responsibleParty);
  }

  /**
   * " - Kari Nordmann" to follow the translated role. The role alone says which
   * side owes the work; the dashboard has to say who, since this card is where
   * a manager looks to find out whether it is them.
   */
  responsibleNameSuffix(row: FlexiComplianceActionPointOverview): string {
    return responsiblePartyNameSuffix(row);
  }

  /** Anything needing an explanation: missed cycle, shifted schedule, or overdue. */
  get exceptionCount(): number {
    return this.rows.filter(r => r.isException).length;
  }

  /** The subset an auditor asks about first. */
  get skippedCount(): number {
    return this.rows.filter(r => r.skippedCycles > 0).length;
  }

  get overdueCount(): number {
    return this.rows.filter(r => r.daysOverdue > 0).length;
  }

  /** Open commitments that are not late — the healthy remainder. */
  get onTrackCount(): number {
    return this.rows.filter(r => !r.isException).length;
  }

  /**
   * Jump to the Action Points tab. With an id, the target row is highlighted
   * there and scrolled to; without one (the footer link) the grid just opens.
   *
   * The id travels as a query param rather than component state because this is
   * a route change — the grid may not even be instantiated yet, and a param
   * survives the reload a deep link implies.
   */
  openActionPoints(actionPointId?: number): void {
    this.router.navigate(['../Flexi/action-points'], {
      relativeTo: this.route,
      queryParams: actionPointId ? { highlight: actionPointId } : {}
    });
  }

  /**
   * Severity tone for the row's left rail. Ordering is the server's job; this
   * only picks the colour, so a missed cycle never renders the same as a row
   * that is merely late.
   */
  toneClass(row: FlexiComplianceActionPointOverview): string {
    if (row.skippedCycles > 0) return 'is-skipped';
    if (row.daysOverdue > 0) return 'is-overdue';
    if (row.scheduleShifted) return 'is-shifted';
    return 'is-ontrack';
  }
}
